/**
 * Which of the rail's sections are open, remembered per browser.
 *
 * A SET, not a single open slot. The four sections answer four unrelated
 * questions - where am I in the log, which label am I filtering on, where is my
 * telephone pointed, which line do calls leave on - and a rail that closed the
 * folders to show the lines would be an accordion in the musical-instrument
 * sense: something that has to be worked to get at what you want.
 *
 * Remembered because the answer is a habit rather than a moment. An operator
 * who never touches the devices closes that section once and should not have to
 * close it again after every reload; one who lives in the label filter keeps it
 * open. Per browser, like the density toggle and the AI panel next door, and
 * for the same reason: it is a property of this desk, not of the account.
 *
 * Settings and lines start CLOSED, and the first of those is not only about
 * tidiness. The audio section holds a live microphone meter, so leaving it open
 * by default would light the tab's recording dot for every operator who opens
 * the call log - see `RailAudio`, which is why the panel only meters while it
 * is genuinely on screen.
 */
import { useCallback, useState } from "react";

export type RailFold = "calls" | "labels" | "settings" | "lines";

const KEY = "crm.calls.rail.folds";
const DEFAULT: RailFold[] = ["calls", "labels"];
const KNOWN: ReadonlySet<string> = new Set<RailFold>(["calls", "labels", "settings", "lines"]);

/** What this browser last left open, or the default. Never throws: a rail that
 *  failed to render because a stored preference was malformed would be a much
 *  worse bug than a section opening in the wrong state. */
function read(): Set<RailFold> {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return new Set(DEFAULT);
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set(DEFAULT);
    // Filtered against the known names rather than trusted: this string outlives
    // the build that wrote it, and a section that has since been renamed must
    // not leave an id nothing reads sitting in the set forever.
    return new Set(parsed.filter((id): id is RailFold => typeof id === "string" && KNOWN.has(id)));
  } catch {
    return new Set(DEFAULT);
  }
}

export function useRailFolds(): { open: Set<RailFold>; toggle: (id: RailFold) => void } {
  const [open, setOpen] = useState<Set<RailFold>>(read);

  const toggle = useCallback((id: RailFold) => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      try {
        window.localStorage.setItem(KEY, JSON.stringify([...next]));
      } catch {
        // A browser with storage switched off still gets the toggle; it simply
        // forgets it on the next reload.
      }
      return next;
    });
  }, []);

  return { open, toggle };
}
